import { apiGet, apiPatch, apiPost } from '../utils/api';
import { fetchFields, fetchTrees } from './traceabilityApi';
import type {
  InspectionSignal,
  InspectionSummary,
  InspectionWorkspaceData,
  StaffOption,
  TreeInspection,
} from '../components/tree-inspections/types';

export async function fetchInspectionWorkspace(isManager: boolean): Promise<InspectionWorkspaceData> {
  const [inspections, summary, fields, trees, users, signals] = await Promise.all([
    apiGet<TreeInspection[]>('/api/tree-inspections'),
    apiGet<InspectionSummary>('/api/tree-inspections/summary'),
    fetchFields(),
    fetchTrees(2000),
    apiGet<StaffOption[]>('/api/users'),
    isManager ? apiGet<InspectionSignal[]>('/api/tree-inspections/signals').catch(() => []) : Promise.resolve([]),
  ]);

  return {
    inspections: Array.isArray(inspections) ? inspections : [],
    summary: summary || {},
    fields,
    trees,
    staff: (Array.isArray(users) ? users : []).filter((user) =>
      ['agronomist', 'agronomist_advisor'].includes(String(user.role || '').toLowerCase()),
    ),
    signals: Array.isArray(signals) ? signals : [],
  };
}

export const fetchInspection = (id: number) =>
  apiGet<TreeInspection>(`/api/tree-inspections/${id}`);

export const createInspection = (payload: Record<string, unknown>) =>
  apiPost<TreeInspection>('/api/tree-inspections', payload);

export const createInspectionFromSignal = (signalId: number, payload: Record<string, unknown>) =>
  apiPost<TreeInspection>(`/api/tree-inspections/signals/${signalId}/from-signal`, payload);

export const updateSignal = (signalId: number, payload: Record<string, unknown>) =>
  apiPatch<InspectionSignal>(`/api/tree-inspections/signals/${signalId}`, payload);

export const updateInspectionStatus = (id: number, status: string, payload: Record<string, unknown> = {}) =>
  apiPatch<TreeInspection>(`/api/tree-inspections/${id}/status`, { status, ...payload });

export const submitInspection = (id: number, payload: Record<string, unknown>) =>
  apiPost<TreeInspection>(`/api/tree-inspections/${id}/submit`, payload);

export const approveInspection = (id: number, reviewComment: string) =>
  apiPost<TreeInspection>(`/api/tree-inspections/${id}/approve`, { review_comment: reviewComment });

export const rejectInspection = (id: number, reviewComment: string) =>
  apiPost<TreeInspection>(`/api/tree-inspections/${id}/reject`, { review_comment: reviewComment });

export const requestInspectionCorrection = (id: number, reason: string) =>
  apiPost<TreeInspection>(`/api/tree-inspections/${id}/request-correction`, {
    correction_reason: reason,
    review_comment: reason,
  });

export const createFollowUpInspection = (id: number, payload: Record<string, unknown>) =>
  apiPost<TreeInspection>(`/api/tree-inspections/${id}/follow-up`, payload);
